import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router';
import api from '../../lib/api';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { DataTable } from './datatable';
import DatatableSortButton from './datatableSortButton';

// Define table columns
export const columns = () => [
  {
    accessorKey: 'full_name',
    header: ({ column }) => <DatatableSortButton column={column}>Namn</DatatableSortButton>,
    cell: (info) => {
      const bunny = info.row.original;
      return (
        <>
          <Link to={`/bunny/${bunny.id}`} className="text-blue-600 font-medium hover:underline">
            {bunny.full_name}
          </Link>
          {bunny.death_date && <Badge variant="destructive" className="ml-3">Avliden</Badge>}
        </>
      );
    },
  },
  {
    accessorKey: 'registration_number',
    header: ({ column }) => <DatatableSortButton column={column}>Reg.nr</DatatableSortButton>,
    sortingFn: (rowA, rowB, columnId) => {
      // Extract the numeric part from "SE-XXX" strings
      const getNumericValue = (regNum) => parseInt(regNum.replace('SE-', ''), 10);
      const a = getNumericValue(rowA.getValue(columnId));
      const b = getNumericValue(rowB.getValue(columnId));

      return a - b; // Sort in ascending order
    },
  },
];

export function BunniesDatatable() {
  const [bunnies, setBunnies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterInput, setFilterInput] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState(''); // Add a state for debounced value
  const [showDeceased, setShowDeceased] = useState(false);
  const [sorting, setSorting] = useState([{ id: 'full_name', desc: false }]);

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalBunnies, setTotalBunnies] = useState(0);

  const location = useLocation(); // React Router hook to detect route changes

  // Debounce logic for filter input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilter(filterInput);
    }, 1000); // 1-second debounce

    return () => {
      clearTimeout(handler); // Cleanup timeout
    };
  }, [filterInput]);

  // Fetch bunnies from backend
  const fetchBunnies = async () => {
    setIsLoading(true);
    try {
      const offset = pagination.pageIndex * pagination.pageSize;
      const limit = pagination.pageSize;

      const response = await api.get('https://skhrf-api.cmdigital.se/v1/bunnies/all', {
        params: {
          offset,
          limit,
          search: debouncedFilter, // Use debouncedFilter here
          showDeceased: showDeceased ? 1 : 0,
        },
      });
      setBunnies(response.data.bunnies || []);
      setTotalBunnies(response.data.totalCount || 0);
    } catch (error) {
      console.error('Error fetching bunnies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBunnies();
  }, [debouncedFilter, showDeceased, pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [debouncedFilter]);

  // Reset state on tab change
  useEffect(() => {
    setFilterInput(''); // Clear the search filter
    setPagination({ pageIndex: 0, pageSize: 10 }); // Reset pagination to the first page
  }, [location.pathname]); // Trigger on route (tab) change

  const filteredBunnies = useMemo(() => {
    return bunnies.filter((bunny) => {
      const isDeceased = bunny?.death_date !== null;
      if (!showDeceased && isDeceased) return false;
      return true;
    });
  }, [bunnies, showDeceased]);

  const totalPages = Math.ceil(totalBunnies / pagination.pageSize);

  const table = useReactTable({
    data: filteredBunnies,
    columns: columns(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    manualPagination: true,
    pageCount: totalPages,
    onPaginationChange: setPagination,
  });

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 py-3">
        <div className="grid grid-cols-2 gap-3">
          <div className='flex flex-col gap-2'>
            <Label htmlFor='searchBunny'>Kanin</Label>
            <Input
              id='searchBunny'
              placeholder="Sök kanin..."
              value={filterInput}
              onChange={(e) => setFilterInput(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="flex gap-2 items-center">
              <Checkbox
                checked={showDeceased}
                onCheckedChange={(val) => setShowDeceased(val)}
              />
              Inkludera avlidna kaniner
            </Label>
          </div>
        </div>
      </div>

      <DataTable table={table} isLoading={isLoading} columns={columns()} skeletonRows={10} />
    </>
  );
}
